import { useEffect, useState } from 'react';
import { THEMES } from '../theme/themes.js';

// Полноэкранная «инициализация» — показывается при смене темы и при обновлении
// сборки (как загрузка в играх). Вид загрузки берётся из theme.loader.kind.
export default function ThemeLoader({ themeId, onDone, minMs = 1100, label }) {
  const th = THEMES[themeId] || {};
  const cfg = th.loader || { kind: 'sweep', label: '' };
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const start = Date.now();
    let raf, doneTimer;
    const tick = () => {
      const p = Math.min(100, ((Date.now() - start) / minMs) * 100);
      setPct(p);
      if (p < 100) raf = requestAnimationFrame(tick);
      else doneTimer = setTimeout(onDone, 140);
    };
    raf = requestAnimationFrame(tick);
    // Чистим и кадр, и финальный таймер — иначе onDone мог дёрнуть setState на снятом родителе.
    return () => { cancelAnimationFrame(raf); clearTimeout(doneTimer); };
  }, [themeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bg = th.bgSolid || '#0d0d10';
  const accent = th.accent || '#c9a24b';
  const text = th.text || '#ece6d6';
  const mono = cfg.mono ? "'JetBrains Mono', ui-monospace, monospace" : (th.headFont || "'Cormorant Garamond', Georgia, serif");
  const shown = label || cfg.label || '';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: bg, display: 'flex',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 26, overflow: 'hidden' }}>
      <Anim kind={cfg.kind} accent={accent} />
      <div style={{ fontFamily: mono, fontSize: cfg.mono ? 15 : 22, letterSpacing: cfg.mono ? '.22em' : '.02em',
                    color: text, textTransform: cfg.mono ? 'uppercase' : 'none', textAlign: 'center', padding: '0 24px' }}>
        {shown}{cfg.mono ? <Dots /> : '…'}
      </div>
      <div style={{ width: 200, height: cfg.mono ? 2 : 3, background: cfg.mono ? '#ffffff14' : '#ffffff10',
                    borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`,
                      background: cfg.mono ? accent : `linear-gradient(90deg,${accent},${th.accentSoft || accent})`,
                      transition: 'width .12s linear', boxShadow: cfg.mono ? `0 0 8px ${accent}` : 'none' }} />
      </div>
      <style>{KEYFRAMES}</style>
    </div>
  );
}

function Dots() {
  const [n, setN] = useState(0);
  useEffect(() => { const i = setInterval(() => setN((x) => (x + 1) % 4), 340); return () => clearInterval(i); }, []);
  return <span>{'.'.repeat(n)}</span>;
}

// Каждый вид загрузки — своя анимация, перекликается с фоном темы.
function Anim({ kind, accent }) {
  if (kind === 'boot') {
    // Кибер-HUD: сканирующая сетка + пульсирующая рамка.
    return (
      <div style={{ position: 'relative', width: 120, height: 120 }}>
        <div style={{ position: 'absolute', inset: 0, border: `1px solid ${accent}`, boxShadow: `0 0 18px ${accent}66, inset 0 0 18px ${accent}22` }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${accent}44 1px,transparent 1px),linear-gradient(90deg,${accent}44 1px,transparent 1px)`, backgroundSize: '20px 20px' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: accent, boxShadow: `0 0 10px ${accent}`, animation: 'tlScan 1.4s linear infinite' }} />
      </div>
    );
  }
  if (kind === 'stars') {
    // Космос: вращающееся кольцо + мерцающие точки.
    const dots = Array.from({ length: 7 }, (_, i) => ({ a: (i / 7) * Math.PI * 2, r: 44 + (i % 3) * 8 }));
    return (
      <div style={{ position: 'relative', width: 120, height: 120, animation: 'tlSpin 3.4s linear infinite' }}>
        {dots.map((d, i) => (
          <span key={i} style={{ position: 'absolute', left: 60 + Math.cos(d.a) * d.r, top: 60 + Math.sin(d.a) * d.r,
                                 width: 3, height: 3, borderRadius: '50%', background: '#fff',
                                 boxShadow: `0 0 6px ${accent}`, animation: `tlTw 1.6s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
        <div style={{ position: 'absolute', inset: 34, border: `1px solid ${accent}55`, borderRadius: '50%' }} />
      </div>
    );
  }
  if (kind === 'orbs') {
    // Крылья: три парящих ореола.
    return (
      <div style={{ position: 'relative', width: 140, height: 90 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ position: 'absolute', top: 20, left: 20 + i * 40, width: 40, height: 40, borderRadius: '50%',
                                background: `radial-gradient(circle,${['#ff9ecf','#9ec8ff','#b5f5d0'][i]}cc,transparent 70%)`,
                                animation: `tlFloat 2.2s ease-in-out ${i * 0.3}s infinite` }} />
        ))}
      </div>
    );
  }
  // sweep (chrome и дефолт): вращающаяся хромовая дуга.
  return (
    <div style={{ width: 90, height: 90, borderRadius: '50%', border: `3px solid ${accent}22`,
                  borderTopColor: accent, animation: 'tlSpin 0.9s linear infinite' }} />
  );
}

const KEYFRAMES = `
@keyframes tlScan { 0%{top:0} 100%{top:100%} }
@keyframes tlSpin { 100%{ transform: rotate(360deg) } }
@keyframes tlTw { 0%,100%{ opacity:.3 } 50%{ opacity:1 } }
@keyframes tlFloat { 0%,100%{ transform: translateY(0); opacity:.7 } 50%{ transform: translateY(-14px); opacity:1 } }
`;
